import { memo } from 'react';
import { Link } from 'react-router-dom';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const OpenJobsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		userStats,
		isAdminView = false,
	}) => {
		// Sample value so admins can lay the card out before stats load; real
		// users get a zero rather than a fake number while the query is in flight.
		const openJobTotal = userStats?.openJobTotal ?? (isAdminView ? 128 : 0);
		const cardData = card.data || {};
		const title = cardData.title || 'Jobs';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#666666';
		const statColor = cardData.statColor || '#000000';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const link = cardData.link || '/browsejobs';

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div className="d-card-head">
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
						}}
					>
						{title}
					</h4>
				</div>

				<div
					className="flex flex-col items-center justify-center flex-1"
					style={{ minHeight: 0 }}
				>
					<div
						className="text-4xl font-bold mb-1"
						style={{
							color: statColor,
							fontFamily: 'Nunito Sans, sans-serif',
						}}
					>
						{openJobTotal.toLocaleString()}
					</div>
					<Link
						to={isEditMode ? '#' : link}
						className="font-bold"
						style={{
							color: textColor,
							fontFamily: 'Nunito Sans, sans-serif',
							fontSize: '14px',
						}}
						onClick={(e) => {
							if (isEditMode) {
								e.preventDefault();
								e.stopPropagation();
							}
						}}
						data-no-drag={isEditMode ? true : undefined}
					>
						Open Jobs
					</Link>
				</div>
			</div>
		);
	}
);

OpenJobsCard.displayName = 'OpenJobsCard';

export default OpenJobsCard;
